export interface ParsedDatabaseUrl {
  type: "mysql" | "postgresql";
  username?: string;
  password?: string;
  host: string;
  port: number;
  database: string;
}

export interface ValidationResult {
  isValid: boolean;
  parsed?: ParsedDatabaseUrl;
  error?: string;
}

export const validateMySQLUrl = (url: string): ValidationResult => {
  if (!url || typeof url !== "string") {
    return {
      isValid: false,
      error: "URL is required and must be a string",
    };
  }

  try {
    const parsedUrl = new URL(url);

    if (parsedUrl.protocol !== "mysql:") {
      return {
        isValid: false,
        error: "URL must be a MySQL connection (mysql://)",
      };
    }

    if (!parsedUrl.hostname) {
      return {
        isValid: false,
        error: "Host is required",
      };
    }

    const database = parsedUrl.pathname.slice(1);
    if (!database) {
      return {
        isValid: false,
        error: "Database name is required",
      };
    }

    // Parse port
    const port = parsedUrl.port ? parseInt(parsedUrl.port, 10) : 3306;

    const parsedData: ParsedDatabaseUrl = {
      type: "mysql",
      username: parsedUrl.username || undefined,
      password: parsedUrl.password || undefined,
      host: parsedUrl.hostname,
      port,
      database,
    };

    return {
      isValid: true,
      parsed: parsedData,
    };
  } catch (error) {
    return {
      isValid: false,
      error: "Invalid URL format",
    };
  }
};

export const validatePostgreSQLUrl = (url: string): ValidationResult => {
  if (!url || typeof url !== "string") {
    return {
      isValid: false,
      error: "URL is required and must be a string",
    };
  }

  try {
    const parsedUrl = new URL(url);

    // Security: Ensure protocol matches expected database type
    if (parsedUrl.protocol !== "postgresql:") {
      return {
        isValid: false,
        error:
          "URL must be a PostgreSQL connection (postgresql:// or postgres://)",
      };
    }

    // Validate required components
    if (!parsedUrl.hostname) {
      return {
        isValid: false,
        error: "Host is required",
      };
    }

    const database = parsedUrl.pathname.slice(1); // Remove leading slash
    if (!database) {
      return {
        isValid: false,
        error: "Database name is required",
      };
    }

    // Parse port
    const port = parsedUrl.port ? parseInt(parsedUrl.port, 10) : 5432;

    const parsedData: ParsedDatabaseUrl = {
      type: "postgresql",
      username: parsedUrl.username || undefined,
      password: parsedUrl.password || undefined,
      host: parsedUrl.hostname,
      port,
      database,
    };

    return {
      isValid: true,
      parsed: parsedData,
    };
  } catch (error) {
    return {
      isValid: false,
      error: "Invalid URL format",
    };
  }
};
